import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSoccerBall, faChess, faLightbulb, faBullseye, faFire, faBalanceScale, faShield, faRobot, faBolt, faDice, faGears, faCheck, faWarning } from '@fortawesome/free-solid-svg-icons';
import { Modal } from './Modal';
import './HelpModal.css';

export const TacticsSetupHelpModal: React.FC<{ isOpen: boolean; onClose: () => void; }> = ({ isOpen, onClose }) => (
    <Modal isOpen={isOpen} onClose={onClose} className="help-modal">
        <h2><FontAwesomeIcon icon={faSoccerBall} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Tactics Setup Guide</h2>
        <p style={{ fontSize: '1.1em', marginBottom: '24px' }}>Master your team's tactical approach. These settings define how your team plays.</p>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faChess} style={{ marginRight: '8px', color: 'var(--info-color)' }} />Formation</h3>
            <p><strong>What it does:</strong> Determines your team's shape on the pitch (e.g., 4-4-2, 4-3-3, 3-5-2).</p>
            <p><strong>How it impacts your team:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Attack rating:</strong> More forwards = higher attack</li>
                <li><strong>Midfield rating:</strong> More midfielders = higher control</li>
                <li><strong>Defense rating:</strong> More defenders = higher defensive stability</li>
            </ul>
            <p className="help-tip" style={{ marginTop: '12px' }}><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Match formations to your squad: have great wingers? Try 4-3-3. Strong center-backs? Consider 5-3-2.</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faBullseye} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Mentality</h3>
            <p><strong>How aggressive your team plays overall:</strong></p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong style={{ color: 'var(--negative-color)' }}><FontAwesomeIcon icon={faFire} style={{ marginRight: '6px' }} />Attacking</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px', lineHeight: '1.4' }}>
                        <div>Attack: +15%</div>
                        <div>Defense: -10%</div>
                        <div style={{ marginTop: '4px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>Go for goals!</div>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong style={{ color: 'var(--info-color)' }}><FontAwesomeIcon icon={faBalanceScale} style={{ marginRight: '6px' }} />Balanced</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px', lineHeight: '1.4' }}>
                        <div>Attack: No change</div>
                        <div>Defense: No change</div>
                        <div style={{ marginTop: '4px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>Steady approach</div>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong style={{ color: 'var(--positive-color)' }}><FontAwesomeIcon icon={faShield} style={{ marginRight: '6px' }} />Defensive</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px', lineHeight: '1.4' }}>
                        <div>Defense: +15%</div>
                        <div>Attack: -10%</div>
                        <div style={{ marginTop: '4px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>Stay solid</div>
                    </div>
                </div>
            </div>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faBolt} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Attacking Focus</h3>
            <p><strong>Where your team creates chances from:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Mixed:</strong> Spread attacks across the pitch - balanced approach</li>
                <li><strong>Through Middle:</strong> Feed your striker directly - good with a strong center-forward</li>
                <li><strong>Down Left/Right Flank:</strong> Use your wingers - exploit wide players</li>
            </ul>
            <p className="help-tip" style={{ marginTop: '12px' }}><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Match to your squad: if your best players are wide, use flanks. Strong striker? Go through the middle.</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faDice} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Passing Style</h3>
            <p><strong>How your team builds play:</strong></p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <div style={{ flex: 1, padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong>Short Passing</strong>
                    <div style={{ marginTop: '8px', fontSize: '0.9em', lineHeight: '1.4' }}>
                        <div style={{ color: 'var(--positive-color)' }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />Higher quality chances</div>
                        <div>Fewer total chances</div>
                        <div style={{ marginTop: '4px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>For skilled, controlled squads</div>
                    </div>
                </div>
                <div style={{ flex: 1, padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong>Mixed</strong>
                    <div style={{ marginTop: '8px', fontSize: '0.9em', lineHeight: '1.4' }}>
                        <div>Balanced approach</div>
                        <div>Balanced chance quality</div>
                        <div style={{ marginTop: '4px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>Safe default choice</div>
                    </div>
                </div>
                <div style={{ flex: 1, padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong>Direct Passing</strong>
                    <div style={{ marginTop: '8px', fontSize: '0.9em', lineHeight: '1.4' }}>
                        <div style={{ color: 'var(--positive-color)' }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />More total chances</div>
                        <div>Lower quality chances</div>
                        <div style={{ marginTop: '4px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>High-risk, high-reward</div>
                    </div>
                </div>
            </div>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faShield} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Defensive Style</h3>
            <p><strong>How your team defends:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Standard:</strong> Balanced defensive approach</li>
                <li><strong>High Press:</strong> Win the ball back early and high up the pitch
                    <ul style={{ marginTop: '4px', marginLeft: '12px', marginBottom: '0' }}>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px', color: 'var(--positive-color)' }} />More possession</li>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faWarning} style={{ marginRight: '4px', color: 'var(--warning-color)' }} />Risky vs. fast teams - can be caught out</li>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faWarning} style={{ marginRight: '4px', color: 'var(--warning-color)' }} />Players get fatigued faster (+20% fitness loss)</li>
                    </ul>
                </li>
                <li><strong>Deep Block:</strong> Defend deep and compact
                    <ul style={{ marginTop: '4px', marginLeft: '12px', marginBottom: '0' }}>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px', color: 'var(--positive-color)' }} />+10% defensive strength</li>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px', color: 'var(--positive-color)' }} />More stable defensively</li>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faWarning} style={{ marginRight: '4px', color: 'var(--warning-color)' }} />Gives more space to opponents</li>
                    </ul>
                </li>
            </ul>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faGears} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Tackling Style</h3>
            <p><strong>How your defenders commit to challenges:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Normal:</strong> Balanced - the safe choice</li>
                <li><strong>Aggressive:</strong> Go hard into challenges
                    <ul style={{ marginTop: '4px', marginLeft: '12px', marginBottom: '0' }}>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px', color: 'var(--positive-color)' }} />Win more ball back</li>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faWarning} style={{ marginRight: '4px', color: 'var(--warning-color)' }} />Higher risk of yellow & red cards</li>
                    </ul>
                </li>
                <li><strong>Cautious:</strong> Play it safe
                    <ul style={{ marginTop: '4px', marginLeft: '12px', marginBottom: '0' }}>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px', color: 'var(--positive-color)' }} />Fewer cards</li>
                        <li style={{ fontSize: '0.9em' }}><FontAwesomeIcon icon={faWarning} style={{ marginRight: '4px', color: 'var(--warning-color)' }} />Lose more battles</li>
                    </ul>
                </li>
            </ul>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faRobot} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Auto-Setup (Assistant Manager)</h3>
            <p><strong>Need tactical advice?</strong> Click <strong>Auto-Setup</strong> to get AI-powered recommendations from your Assistant Manager!</p>
            <div style={{ backgroundColor: 'var(--primary-color)', padding: '12px', borderRadius: '4px', marginTop: '12px' }}>
                <p style={{ margin: '0 0 8px 0' }}>Your Assistant Manager will analyze:</p>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    <li>Your team's strengths (attack, midfield, defense)</li>
                    <li>Your next opponent's tactics and formation</li>
                    <li>Optimal formation & mentality for the matchup</li>
                    <li>Best defensive, passing, and attacking styles</li>
                </ul>
            </div>
            <p style={{ marginTop: '12px', fontSize: '0.95em', color: 'var(--accent-text-color)' }}>
                <strong><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} />Tip:</strong> Higher-level Assistant Managers give more accurate recommendations! Upgrade your assistant manager in the Manager tab.
            </p>
        </div>

        <div className="help-section" style={{ backgroundColor: 'rgba(0, 255, 155, 0.1)', padding: '12px', borderRadius: '4px' }}>
            <h3 style={{ marginTop: 0 }}><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Quick Tips</h3>
            <ul style={{ margin: '8px 0 0 20px', lineHeight: '1.6' }}>
                <li>Use <strong>Auto-Setup</strong> for instant AI recommendations based on your opponent</li>
                <li>Go <strong>Attacking</strong> vs. weaker teams, <strong>Defensive</strong> vs. stronger ones</li>
                <li>Experiment with different formations to find what suits your squad</li>
                <li><strong>High Press</strong> is aggressive but tires players - use sparingly</li>
                <li><strong>Deep Block</strong> is safer and better for defending leads</li>
                <li>Your Assistant Manager's recommendations get better as you upgrade them!</li>
            </ul>
        </div>

        <div className="modal-actions">
            <button className="button primary" onClick={onClose}>Got it!</button>
        </div>
    </Modal>
);
